import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, Check, X } from "lucide-react";
import { COMPARISONS, type ComparisonRow } from "@/content/comparisons";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AuthorBox } from "@/components/AuthorBox";
import { Section } from "@/components/Section";
import { faqSchema } from "@/lib/schema";

export const Route = createFileRoute("/compare/$slug")({
  loader: ({ params }) => {
    const c = COMPARISONS.find((x) => x.slug === params.slug);
    if (!c) throw notFound();
    return { c };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.c;
    if (!c) return { meta: [{ title: "Comparison not found" }] };
    return buildSeo({
      title: `${c.metaTitle} | Freelancer Tamal`,
      description: c.description,
      path: `/compare/${c.slug}`,
      ogEyebrow: "Comparison",
      ogTitle: c.title,
    });
  },
  component: ComparePage,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl">Comparison not found</h1>
      <Link to="/compare" className="mt-6 inline-flex text-primary">← All comparisons</Link>
    </div>
  ),
});

function ComparePage() {
  const { c } = Route.useLoaderData();

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Compare", path: "/compare" },
          { name: c.title, path: `/compare/${c.slug}` },
        ]}
      />
      <JsonLd
        data={faqSchema(c.faq)}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE.url}/compare/${c.slug}#webpage`,
          name: c.title,
          url: `${SITE.url}/compare/${c.slug}`,
          description: c.description,
          isPartOf: { "@id": `${SITE.url}/#website` },
          about: {
            "@type": "Thing",
            name: `${c.themHeading} vs. Freelancer Tamal`,
          },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", ".aeo-speakable"],
          },
        }}
      />

      <article className="container-x pt-12 pb-16 max-w-4xl">
        <Link to="/compare" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> All comparisons
        </Link>

        <header className="mt-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary">
            Comparison
          </div>
          <h1 className="mt-6 font-display text-4xl md:text-6xl leading-[1.05]">{c.title}</h1>
          <p className="mt-8 text-xl text-muted-foreground">{c.lead}</p>

          <div className="mt-10 rounded-2xl bg-card border border-border p-6 -mx-2">
            <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2">Quick answer</div>
            <p className="text-foreground aeo-speakable">{c.quickAnswer}</p>
          </div>
        </header>

        {/* Comparison table */}
        <section className="mt-16">
          <h2 className="font-display text-3xl text-foreground mb-8">Side by side</h2>
          <div className="overflow-x-auto rounded-3xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-card/50">
                  <th className="text-left p-4 font-medium text-muted-foreground w-1/3">Feature</th>
                  <th className="text-left p-4 font-medium text-primary">Freelancer Tamal</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">vs. {c.themHeading}</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r: ComparisonRow) => (
                  <tr key={r.feature} className="border-b border-border last:border-b-0 align-top">
                    <td className="p-4 font-medium text-foreground" dangerouslySetInnerHTML={{ __html: r.feature }} />
                    <td className="p-4 text-foreground" dangerouslySetInnerHTML={{ __html: r.tamal }} />
                    <td className="p-4 text-muted-foreground" dangerouslySetInnerHTML={{ __html: r.them }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* When to pick each */}
        <section className="mt-16 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-primary/40 bg-primary/5 p-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-3">
              <Check size={12} /> Pick Tamal when
            </div>
            <ul className="mt-2 space-y-3">
              {c.whenToPickTamal.map((line: string) => (
                <li key={line} className="flex items-start gap-2 text-sm text-foreground">
                  <Check size={14} className="mt-1 text-primary shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card/50 p-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
              <X size={12} /> Pick {c.themLabel} when
            </div>
            <ul className="mt-2 space-y-3">
              {c.whenToPickThem.map((line: string) => (
                <li key={line} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <X size={14} className="mt-1 opacity-60 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20 pt-10 border-t border-border">
          <h2 className="font-display text-3xl text-foreground">Frequently asked</h2>
          <div className="mt-8 space-y-6">
            {c.faq.map((f: { q: string; a: string }) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <div className="font-medium text-foreground">{f.q}</div>
                <p className="mt-3 text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-16 rounded-3xl border border-primary/30 bg-card p-8 lg:p-10">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">Ready to decide?</div>
          <h3 className="mt-3 font-display text-3xl">
            Book a free 20-minute call — no sales pitch.
          </h3>
          <p className="mt-4 text-muted-foreground">
            Bring your current SEO setup. We'll diagnose what's working, what isn't,
            and whether a freelance consultant is actually the right shape for your team.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
          >
            Book the call <ArrowUpRight size={16} />
          </Link>
        </div>

        <AuthorBox />
      </article>

      <Section eyebrow="Other comparisons" title="Still deciding? Compare another option.">
        <div className="grid md:grid-cols-2 gap-6">
          {COMPARISONS.filter((x) => x.slug !== c.slug).map((other) => (
            <Link
              key={other.slug}
              to="/compare/$slug"
              params={{ slug: other.slug }}
              className="group rounded-3xl border border-border p-8 hover:bg-card transition"
            >
              <div className="text-xs uppercase tracking-[0.18em] text-primary">Comparison</div>
              <h3 className="mt-4 font-display text-2xl">{other.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{other.tagline}</p>
              <ArrowUpRight size={16} className="mt-6 text-muted-foreground group-hover:text-primary transition" />
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}
